import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { evaluateAndLogExperiment } from 'stores/utils';
import { SearchCriteriaModel } from 'stores/models';

import { FilterModel, FilterOptionModel, SortAndFilterModel } from 'components/common/search-form/fields/filter-types';
import { UitkHeadingSize } from 'uitk-react-text/dist/types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkRadioButton, UitkRadioButtonGroup } from 'uitk-react-radio-button';
import { UitkSpacing } from 'uitk-react-spacing';

import { arrayToMatrix } from 'utils/array-utils';

import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';

const RadioGroupComponent = inject(
  'context',
  'experiment',
  'page'
)(
  ({
    context,
    experiment,
    page,
    headerSize,
    model,
    modelKey,
    sortAndFilterOptions,
    updateModel,
    handleChange,
    keepExpanded,
  }: {
    context;
    experiment;
    page;
    headerSize: UitkHeadingSize;
    model: SearchCriteriaModel;
    modelKey: string;
    sortAndFilterOptions: SortAndFilterModel;
    updateModel: any;
    handleChange?: (evt) => void;
    keepExpanded?: boolean;
  }) => {
    const track = useClickTracker();

    const radioChange = evt => {
      const target = evt.target;
      const { value } = target;

      const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
      const radioGroupName: string = radioGroup && radioGroup.title;
      const modelValue = model && model[radioGroupName];

      if (!(typeof updateModel === 'function') || typeof modelValue === 'undefined') {
        return;
      }

      updateModel({
        target: {
          name: radioGroupName,
          value,
        },
      });

      if (
        radioGroupName === 'poi' &&
        evaluateAndLogExperiment('Default_Sort_to_Distance_for_Popular_Location_Filter', context, experiment, page) === 1
      ) {
        updateModel({
          target: {
            name: 'sort',
            value: 'DISTANCE',
          },
        });
      }
    };

    const radioGroup = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupOptions = radioGroup && radioGroup.options;
    const radioGroupName: string = radioGroup && radioGroup.title;
    const showListState = radioGroup?.showListOptions;

    const getInitialViewExpandedState = () =>
      keepExpanded &&
      showListState?.hiddenThreshold &&
      radioGroupOptions.slice(showListState.hiddenThreshold).some(radioOption => radioOption.isSelected);

    const [viewExpanded, setViewExpanded] = React.useState(getInitialViewExpandedState());
    if (!radioGroupOptions || radioGroupOptions.length === 0) {
      return null;
    }

    const radios = (
      <Radios
        handleChange={handleChange || radioChange}
        model={model}
        modelKey={modelKey}
        sortAndFilterOptions={sortAndFilterOptions}
        children={Array.from({ length: radioGroupOptions.length }, (v, i) => i)}
      />
    );

    return (
      <UitkSpacing margin={{ blockstart: 'six' }}>
        <fieldset>
          <UitkSpacing margin={{ blockend: 'two' }}>
            <legend id={`${radioGroupName}-legend`} className="filter-title">
              <UitkHeading size={headerSize} tag="h4">
                {radioGroup.label}
              </UitkHeading>
            </legend>
          </UitkSpacing>
          {showListState.hiddenThreshold && radioGroupOptions.length > showListState.hiddenThreshold ? (
            <>
              <span data-stid="radio-group-expando-peek"></span>
              <UitkExpandoPeek
                items={showListState.hiddenThreshold}
                collapseLabel={showListState.seeLessLabel}
                expandLabel={showListState.seeMoreLabel}
                handleClick={() => setViewExpanded(!viewExpanded)}
                analytics={{
                  id: radioGroup.title,
                  callback: id => {
                    const idArr = id.split('.');
                    track(`${idArr[0]}.ViewAllFilters.${idArr[1]}`, `${radioGroup.title}.ViewAllFilters`);
                  },
                }}
                isVisible={viewExpanded}
                hideScrim
              >
                {radios}
              </UitkExpandoPeek>
            </>
          ) : (
            radios
          )}
        </fieldset>
      </UitkSpacing>
    );
  }
);

export const Radios = observer(({ handleChange, model, modelKey, sortAndFilterOptions }) => {
  const track = useClickTracker();

  const singleColumnNames = ['guestRating'];
  const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const radioGroupOptions: FilterOptionModel[] = radioGroup && radioGroup.options;
  const radioGroupName = radioGroup?.title;
  const radioGroupAltName = radioGroup?.name;
  const value = model?.[radioGroupName] || model?.[radioGroupAltName] || '';
  const group = radioGroup.label || radioGroupName;

  let hiddenField;
  if (value) {
    if (!radioGroupOptions.some(option => option.value === value)) {
      hiddenField = <input type="radio" hidden={true} name={radioGroupName} checked={false} value={value} />;
    }
  }

  const getRadioButtons = (columns: UitkLayoutGridColumnRow) => {
    const radioGroupOptionsArray = arrayToMatrix(radioGroupOptions, columns);

    return (
      <UitkRadioButtonGroup
        group={group}
        onChange={event => {
          const value = event.currentTarget.value;
          track(`${radioGroupName}.${value}`, `${radioGroupName}.${value}`);
          handleChange(event);
        }}
        selected={value}
      >
        {radioGroupOptionsArray.map((options, idx) => (
          <UitkSpacing padding={{ inline: 'one' }} key={`${radioGroupName}-item-${idx}`}>
            <UitkLayoutGrid columns={columns} data-stid={`radio-group-columns-${columns}`}>
              {options.map((option, i) => (
                <UitkLayoutGridItem key={`${radioGroupName}-item-${i}`}>
                  <UitkRadioButton
                    description={option.description}
                    id={`radio-${radioGroupName}-${option.value}`}
                    key={`${radioGroupName}-${idx}-${option.value}`}
                    name={radioGroupName}
                    value={option.value}
                    data-stid={`radio-${radioGroupName}-${option.value}`}
                    checked={value === option.value}
                  >
                    {option.label}
                  </UitkRadioButton>
                </UitkLayoutGridItem>
              ))}
            </UitkLayoutGrid>
          </UitkSpacing>
        ))}
      </UitkRadioButtonGroup>
    );
  };

  const getRadioButtonLayout = () => (
    <Viewport>
      <ViewSmall>{getRadioButtons(singleColumnNames.includes(radioGroupName) ? 1 : 2)}</ViewSmall>
      <ViewLarge>{getRadioButtons(1)}</ViewLarge>
    </Viewport>
  );

  return (
    <React.Fragment>
      <Experiment name="PWA_JPSmallCrafts_HoldOut">
        <ExperimentControl>{getRadioButtons(1)}</ExperimentControl>
        <ExperimentVariant bucket={1}>{getRadioButtonLayout()}</ExperimentVariant>
        <ExperimentVariant bucket={2}>{getRadioButtonLayout()}</ExperimentVariant>
      </Experiment>
      {hiddenField}
    </React.Fragment>
  );
});

export const RadioGroup = FormFieldBinding(RadioGroupComponent);
